import {
  CREDIT_WALLET_BALANCE,
  CREDIT_WALLET_DRAW_ORDER,
  CreditBucket,
  CreditWallet,
} from "@/app/types/billing/CreditWallet";
import { PlanSettings } from "@/app/types/billing/PlanSettings";
import { UPlan } from "@/app/utils/UPlan";
import { Credits } from "@/app/vars/Credits";

export class UWallet {
  public static callCredits(wallet: CreditWallet): number {
    return wallet.callAllowance + wallet.callRollover + wallet.callBalance;
  }

  public static prospectCredits(wallet: CreditWallet): number {
    return wallet.prospectAllowance + wallet.prospectRollover + wallet.prospectBalance;
  }

  public static total(wallet: CreditWallet): number {
    return (
      wallet.callAllowance +
      wallet.prospectAllowance +
      wallet.callRollover +
      wallet.prospectRollover +
      wallet.callBalance +
      wallet.prospectBalance
    );
  }

  public static spendable(wallet: CreditWallet, bucket?: CreditBucket): number {
    if (bucket === CreditBucket.CALL) {
      return UWallet.callCredits(wallet);
    }
    if (bucket === CreditBucket.PROSPECT) {
      return UWallet.prospectCredits(wallet);
    }
    return UWallet.total(wallet);
  }

  public static hasAllowance(wallet: CreditWallet): boolean {
    return (
      wallet.callAllowance > 0 ||
      wallet.prospectAllowance > 0 ||
      wallet.callRollover > 0 ||
      wallet.prospectRollover > 0
    );
  }

  public static oneDecimal(value: number): number {
    return Math.round(value * 10) / 10;
  }

  public static hoursFromCredits(credits: number): number {
    return UWallet.oneDecimal(credits / Credits.ANALYSIS_CREDITS_PER_HOUR);
  }

  public static usedCallHours(wallet: CreditWallet, plan: PlanSettings): number {
    const granted = UPlan.callAllowanceCredits(plan);
    return UWallet.hoursFromCredits(Math.max(0, granted - wallet.callAllowance));
  }

  public static usedProspects(wallet: CreditWallet, plan: PlanSettings): number {
    return Math.max(0, plan.prospectsPerMonth - wallet.prospectAllowance);
  }

  public static usedFreeCallHours(wallet: CreditWallet): number {
    return UWallet.hoursFromCredits(Math.max(0, Credits.FREE_CALL_CREDITS - wallet.callBalance));
  }

  public static usedFreeProspects(wallet: CreditWallet): number {
    const granted = Credits.FREE_PROSPECT_CREDITS * Credits.EXTENSION_PROFILES_PER_CREDIT;
    return Math.max(0, granted - UWallet.spendableProspects(wallet, false));
  }

  public static callHours(wallet: CreditWallet): number {
    return Math.floor(
      (wallet.callAllowance + wallet.callRollover) / Credits.ANALYSIS_CREDITS_PER_HOUR,
    );
  }

  public static prospectCount(wallet: CreditWallet): number {
    return (
      (wallet.prospectAllowance + wallet.prospectRollover) * Credits.EXTENSION_PROFILES_PER_CREDIT
    );
  }

  // What an account may draw on. Only the added balance survives a plan that is not paid
  // for: the allowance columns keep their value until Stripe settles or deletes the
  // subscription, and spending them in the meantime is spending what was never paid.
  public static drawOrder(
    bucket: CreditBucket | undefined,
    planActive: boolean,
  ): (keyof CreditWallet)[] {
    if (!planActive) {
      return bucket ? [CREDIT_WALLET_BALANCE[bucket]] : ["callBalance", "prospectBalance"];
    }
    return bucket
      ? CREDIT_WALLET_DRAW_ORDER[bucket]
      : [
          ...CREDIT_WALLET_DRAW_ORDER[CreditBucket.CALL],
          ...CREDIT_WALLET_DRAW_ORDER[CreditBucket.PROSPECT],
        ];
  }

  public static spendableHours(wallet: CreditWallet, planActive: boolean): number {
    const credits = planActive ? UWallet.callCredits(wallet) : wallet.callBalance;
    return UWallet.hoursFromCredits(credits);
  }

  public static spendableProspects(wallet: CreditWallet, planActive: boolean): number {
    const credits = planActive ? UWallet.prospectCredits(wallet) : wallet.prospectBalance;
    return credits * Credits.EXTENSION_PROFILES_PER_CREDIT;
  }
}
